import React, { createContext } from 'react';
import { getCharactersProgress } from 'components/modules/Reports/utils/reports-util';
import useStudentProgress from 'graph/state/query-hooks/use-student-progress';
import useClassrooms from 'state/hooks/use-classrooms';
import useTeachingOrder from 'state/hooks/use-teaching-order';
import {
  useQueryParams,
  StringParam,
  NumberParam,
  DelimitedNumericArrayParam,
  withDefault,
} from 'use-query-params';
import { ReportParams } from './types';
import useUserStore from '@monorepo/shared/src/graph/hooks/use-user';
import { isTrialLicense } from '@monorepo/shared/src/graph/selectors/license-selector';
import { useNavigate } from 'react-router-dom';

export const ReportsContext = createContext<any>([]);

const ReportsProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const [{ activeCharacters }] = useTeachingOrder();
  const [{ activeClassroom }] = useClassrooms();
  const [{ me }] = useUserStore();
  const license = me?.licenses?.[0];
  const isTrialUser = isTrialLicense(license);
  if (isTrialUser) navigate('/home');

  const students = activeClassroom?.students ?? [];

  const [queryParams, setQuery] = useQueryParams({
    character: StringParam,
    selectedStudentId: NumberParam,
    studentIds: withDefault(
      DelimitedNumericArrayParam,
      students.map((student) => student.id)
    ),
  });

  const filters = queryParams as ReportParams;

  const [{ progressData, isLoading, refetch }] = useStudentProgress({
    studentId: filters.studentIds,
    classroomId: activeClassroom?.id as number,
  });

  const filteredStudents = students.filter((student) => filters.studentIds.includes(student.id));
  const charactersProgress = getCharactersProgress(filteredStudents, progressData);

  const selectedCharacter =
    filters.character && activeCharacters.find((c) => filters.character === c.character);
  const selectedStudent = students.find((student) => {
    const studentId =
      filters.selectedStudentId ?? (filters.studentIds.length === 1 && filters.studentIds[0]);
    return student.id === studentId;
  });

  const state = {
    progressData,
    isLoading,
    filters,
    filteredStudents,
    charactersProgress,
    selectedCharacter,
    selectedStudent,
  };
  if (!activeClassroom || !activeClassroom?.id) return null;

  return (
    <ReportsContext.Provider
      value={[
        state,
        {
          setQuery,
          refetch,
        },
      ]}
    >
      {children}
    </ReportsContext.Provider>
  );
};

export default ReportsProvider;
